import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type MfaFactor = {
  id: string;
  friendly_name?: string | null;
  factor_type: string;
  status: string;
  created_at: string;
};

export type MfaState = {
  loading: boolean;
  enrolled: boolean;
  verifiedFactor: MfaFactor | null;
  unverifiedFactor: MfaFactor | null;
  currentLevel: "aal1" | "aal2" | null;
  nextLevel: "aal1" | "aal2" | null;
  needsChallenge: boolean;
};

export const useMfa = () => {
  const [state, setState] = useState<MfaState>({
    loading: true,
    enrolled: false,
    verifiedFactor: null,
    unverifiedFactor: null,
    currentLevel: null,
    nextLevel: null,
    needsChallenge: false,
  });

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    const [{ data: factorsData }, { data: aalData }] = await Promise.all([
      supabase.auth.mfa.listFactors(),
      supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
    ]);
    const totp = (factorsData?.totp ?? []) as MfaFactor[];
    const verified = totp.find((f) => f.status === "verified") ?? null;
    const unverified = totp.find((f) => f.status !== "verified") ?? null;
    setState({
      loading: false,
      enrolled: !!verified,
      verifiedFactor: verified as MfaFactor | null,
      unverifiedFactor: unverified as MfaFactor | null,
      currentLevel: (aalData?.currentLevel as "aal1" | "aal2") ?? null,
      nextLevel: (aalData?.nextLevel as "aal1" | "aal2") ?? null,
      needsChallenge:
        aalData?.currentLevel === "aal1" && aalData?.nextLevel === "aal2",
    });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const enroll = useCallback(async (friendlyName = "Authenticator app") => {
    // Clean up any previous unverified factor first
    const { data: existing } = await supabase.auth.mfa.listFactors();
    const stale = existing?.totp?.find((f) => f.status !== "verified");
    if (stale) {
      await supabase.auth.mfa.unenroll({ factorId: stale.id });
    }
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: "totp",
      friendlyName,
    });
    if (error) throw error;
    return data; // { id, type, totp: { qr_code, secret, uri } }
  }, []);

  const verifyEnroll = useCallback(async (factorId: string, code: string) => {
    const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({
      factorId,
    });
    if (chErr) throw chErr;
    const { error } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: ch.id,
      code,
    });
    if (error) throw error;
    await refresh();
  }, [refresh]);

  const challengeAndVerify = useCallback(
    async (factorId: string, code: string) => {
      const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({
        factorId,
      });
      if (chErr) throw chErr;
      const { error } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: ch.id,
        code,
      });
      if (error) throw error;
      await refresh();
    },
    [refresh],
  );

  const unenroll = useCallback(
    async (factorId: string) => {
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw error;
      await refresh();
    },
    [refresh],
  );

  return { ...state, refresh, enroll, verifyEnroll, challengeAndVerify, unenroll };
};
